/* Saying that something happened.
 *
 * Every write on the access panel goes to Entra and takes a second or two, and
 * until now the only sign one had run was a control going quiet — a select
 * that stopped responding, a button whose label changed. Setting somebody's
 * hub level looked identical whether it had worked, was still going, or had
 * failed. So people press it again, and a group write is the last thing that
 * should be asked to survive being pressed twice.
 *
 * The same argument the sync button already makes in ui.module.scss: greying a
 * control out says only "you cannot press this", which looks the same as
 * broken.
 *
 * A toast here is not fire-and-forget. `show` hands back a handle, so the
 * toast that says a thing is happening becomes the toast that says how it
 * went, in place — one line that changes rather than two that stack. That is
 * what makes a slow write legible: it appears the instant you choose, and it
 * resolves where you are already looking.
 *
 * Deliberately not a library. Three states and one animation is smaller than
 * the dependency would be, and this reuses the spinner and the tone tokens
 * that are already here.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import s from './ui.module.scss';

export type ToastKind = 'ok' | 'error' | 'busy' | 'warn';

export interface ToastHandle {
  /** Turn a pending toast into its own result, rather than stacking a second
   *  one underneath it. */
  update(text: string, kind: ToastKind): void;
  close(): void;
}

interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
}

type Show = (text: string, kind?: ToastKind) => ToastHandle;

const NOOP: ToastHandle = { update: () => undefined, close: () => undefined };

/* A no-op default rather than a thrown error. A component that toasts should
   still render in a test that did not wrap it, and the screen tests mount
   panels directly. */
const ToastContext = createContext<Show>(() => NOOP);

export function useToast(): Show {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const close = useCallback(
    (id: number) => {
      clearTimer(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
    },
    [clearTimer],
  );

  /* How long each kind stays.
   *
   * Busy never leaves on its own — it is replaced by its own result, and one
   * that timed out would leave somebody certain a write was still running.
   * An error stays long enough to be read and copied, because it carries the
   * service's own words and is the only place they appear. */
  const arm = useCallback(
    (id: number, kind: ToastKind) => {
      clearTimer(id);
      if (kind === 'busy') return;
      timers.current.set(id, setTimeout(() => close(id), kind === 'error' ? 10000 : kind === 'warn' ? 8000 : 4500));
    },
    [clearTimer, close],
  );

  const show = useCallback<Show>(
    (text, kind = 'ok') => {
      const id = nextId.current;
      nextId.current += 1;
      setItems((xs) => xs.concat([{ id, text, kind }]));
      arm(id, kind);
      return {
        update: (t, k) => {
          setItems((xs) => xs.map((x) => (x.id === id ? { ...x, text: t, kind: k } : x)));
          arm(id, k);
        },
        close: () => close(id),
      };
    },
    [arm, close],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {/* polite, not assertive: these report the result of something the person
          just did themselves, so interrupting whatever a screen reader is
          already saying would be rude rather than helpful. */}
      <div className={s.toasts} role="status" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={[
              s.toast,
              t.kind === 'error' ? s.toastError : t.kind === 'busy' ? s.toastBusy : t.kind === 'warn' ? s.toastWarn : s.toastOk,
            ].join(' ')}
          >
            <i
              className={[
                'ti',
                t.kind === 'error'
                  ? 'ti-alert-triangle'
                  : t.kind === 'warn'
                    ? 'ti-bell-ringing'
                  : t.kind === 'busy'
                    ? 'ti-loader-2'
                    : 'ti-check',
                t.kind === 'busy' ? s.spin : '',
              ].join(' ')}
              aria-hidden
            />
            <span className={s.toastText}>{t.text}</span>
            {/* Busy has nothing to dismiss yet — closing it would hide a write
                that is still running. */}
            {t.kind !== 'busy' && (
              <button
                type="button"
                className={s.toastClose}
                onClick={() => close(t.id)}
                aria-label="Dismiss"
              >
                <i className="ti ti-x" aria-hidden />
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
